import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface TourStep {
  title: string;
  content: string;
  targetSelector?: string;       // CSS selector to highlight
  targetPath?: string;            // Navigate to this path before showing step
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  adminOnly?: boolean;
}

interface Props {
  isAdmin: boolean;
  onComplete: () => void;
}

const TOUR_STEPS: TourStep[] = [
  {
    title: 'Welcome to CGS Expense Reports!',
    content: 'Alright, let\'s get you up to speed. This quick tour will walk you through the main areas of the app \u2014 nothing too intense, just the lay of the land. Hit Escape or the X if you want to bail at any point. No hard feelings.',
    position: 'center',
  },
  {
    title: 'Your Navigation Bar',
    content: 'This is your main navigation \u2014 it lives at the top of every page. Dashboard, New Report, History \u2014 that\'s the core loop you\'ll use to go from "I have receipts" to "I have an expense report." Simple as that.',
    targetSelector: 'nav',
    position: 'bottom',
    targetPath: '/dashboard',
  },
  {
    title: 'The Dashboard',
    content: 'This is home base. You\'ll see your recent receipts here as a gallery \u2014 handy for making sure everything got uploaded correctly. Think of it as your "did I forget anything?" checkpoint before building a report.',
    position: 'center',
    targetPath: '/dashboard',
  },
  {
    title: 'New Report',
    content: 'This is where the magic happens. Creating a report is a 4-step wizard: set your dates, pick your receipts, review the line items, and hit Generate. The app handles the GUSTO formatting, the PDF merging, the mileage math \u2014 all the stuff nobody wants to do manually. Upload your receipt files here in the first step.',
    targetPath: '/new-report',
    position: 'center',
  },
  {
    title: 'History',
    content: 'Every report you\'ve ever generated lives here. Need to download that PDF again? Done. Need to fix a line item and regenerate? Also done. Click any report to open it up and make changes.',
    targetPath: '/history',
    position: 'center',
  },
  // Admin-only: Accounting with all 4 tabs
  {
    title: 'Accounting \u2014 The Big Picture',
    content: 'Since you\'re an admin, you get the Accounting tab. This is command central for seeing expenses across the entire organization. It\'s got four sub-tabs, and we\'re about to walk through each one. Buckle up.',
    targetPath: '/accounting',
    position: 'center',
    adminOnly: true,
  },
  {
    title: 'Accounting: Dashboard Tab',
    content: 'The Dashboard tab gives you interactive charts \u2014 spending by month, by user, by event. Click any chart segment to filter the receipt details table below. Ctrl+click to multi-select. It\'s basically your "where did all the money go?" view. You can also export everything to CSV.',
    targetSelector: '[data-tour="accounting-tabs"]',
    position: 'bottom',
    targetPath: '/accounting',
    adminOnly: true,
  },
  {
    title: 'Accounting: Events Tab',
    content: 'Events group expense reports together \u2014 think "Esri Dev Summit 2026" or "Client Onsite Houston." You can create events manually, or let AI do the heavy lifting with "Auto-Generate from Reports." It reads all the report titles and dates, figures out what goes together, and auto-assigns everything. Pretty slick.',
    targetSelector: '[data-tour="accounting-tabs"]',
    position: 'bottom',
    targetPath: '/accounting',
    adminOnly: true,
  },
  {
    title: 'Accounting: Admins Tab',
    content: 'This is where you manage who has accounting access. Search for CGS team members, add them as admins, remove them if needed. Only @cultivategeospatial.com accounts allowed \u2014 no wildcards here.',
    targetSelector: '[data-tour="accounting-tabs"]',
    position: 'bottom',
    targetPath: '/accounting',
    adminOnly: true,
  },
  {
    title: 'Accounting: Logs Tab',
    content: 'The Logs tab shows you what\'s been happening behind the scenes \u2014 receipt uploads, report generations, event assignments. Useful for debugging when someone says "I uploaded that receipt three times and it\'s not showing up." (Spoiler: it usually is.)',
    targetSelector: '[data-tour="accounting-tabs"]',
    position: 'bottom',
    targetPath: '/accounting',
    adminOnly: true,
  },
  // Back to general
  {
    title: 'Need Help? We Got You.',
    content: 'See that little ? icon up in the top-right corner? That\'s your help menu. You can pull up the full documentation, replay this tour, or \u2014 if you\'re an admin \u2014 check out the accounting-specific docs. It\'s all there when you need it.',
    position: 'center',
    targetPath: '/dashboard',
  },
  {
    title: 'You\'re Good to Go!',
    content: 'That\'s the grand tour. Head to New Report to start building an expense report, or check out the Dashboard to see what\'s already there. And remember \u2014 the ? icon is always just a click away. Now go expense some things.',
    position: 'center',
  },
];

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 9998, pointerEvents: 'none',
  },
  backdrop: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', zIndex: 9997,
  },
  tooltip: {
    position: 'fixed', zIndex: 9999, background: 'white', borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)', maxWidth: 440, width: '90vw',
    animation: 'tourFadeIn 0.25s ease-out', pointerEvents: 'auto' as const,
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px 0', gap: 12,
  },
  title: { fontSize: 16, fontWeight: 700, color: '#1a365d', margin: 0 },
  closeBtn: {
    background: 'none', border: 'none', fontSize: 20, color: '#999',
    cursor: 'pointer', padding: '0 4px', lineHeight: 1,
  },
  body: { padding: '12px 20px 16px', fontSize: 14, lineHeight: 1.6, color: '#4a5568' },
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0 20px 16px', gap: 12,
  },
  progress: { fontSize: 12, color: '#999' },
  buttons: { display: 'flex', gap: 8 },
  btnPrimary: {
    background: '#0078d4', color: 'white', border: 'none', padding: '8px 18px',
    borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600,
  },
  btnSecondary: {
    background: 'white', color: '#333', border: '1px solid #cbd5e0', padding: '8px 18px',
    borderRadius: 6, fontSize: 13, cursor: 'pointer',
  },
  dots: { display: 'flex', gap: 4, justifyContent: 'center', padding: '0 20px 16px' },
  dot: { width: 6, height: 6, borderRadius: '50%', background: '#e2e8f0' },
  dotActive: { width: 6, height: 6, borderRadius: '50%', background: '#0078d4' },
};

export default function GuidedTour({ isAdmin, onComplete }: Props) {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Filter steps based on admin status
  const steps = TOUR_STEPS.filter(s => !s.adminOnly || isAdmin);

  const step = steps[currentStep];

  const finish = useCallback(() => {
    localStorage.setItem('cgs-expense-tour-completed', 'true');
    onComplete();
  }, [onComplete]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (currentStep < steps.length - 1) setCurrentStep(c => c + 1);
        else finish();
      } else if (e.key === 'ArrowLeft') {
        if (currentStep > 0) setCurrentStep(c => c - 1);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [currentStep, steps.length, finish]);

  // Navigate to target path if needed
  useEffect(() => {
    if (step?.targetPath && location.pathname !== step.targetPath) {
      navigate(step.targetPath);
    }
  }, [step, location.pathname, navigate]);

  // Calculate tooltip position
  const getTooltipStyle = (): React.CSSProperties => {
    if (!step?.targetSelector || step.position === 'center') {
      return {
        ...styles.tooltip,
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const el = document.querySelector(step.targetSelector);
    if (!el) {
      return {
        ...styles.tooltip,
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const rect = el.getBoundingClientRect();
    const pos = step.position || 'bottom';

    switch (pos) {
      case 'bottom':
        return {
          ...styles.tooltip,
          top: rect.bottom + 12,
          left: Math.max(16, Math.min(rect.left, window.innerWidth - 456)),
        };
      case 'top':
        return {
          ...styles.tooltip,
          bottom: window.innerHeight - rect.top + 12,
          left: Math.max(16, Math.min(rect.left, window.innerWidth - 456)),
        };
      case 'right':
        return {
          ...styles.tooltip,
          top: rect.top,
          left: rect.right + 12,
        };
      case 'left':
        return {
          ...styles.tooltip,
          top: rect.top,
          right: window.innerWidth - rect.left + 12,
        };
      default:
        return {
          ...styles.tooltip,
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
        };
    }
  };

  // Highlight target element
  const getHighlightStyle = (): React.CSSProperties | null => {
    if (!step?.targetSelector) return null;
    const el = document.querySelector(step.targetSelector);
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      position: 'fixed',
      top: rect.top - 4,
      left: rect.left - 4,
      width: rect.width + 8,
      height: rect.height + 8,
      border: '2px solid #0078d4',
      borderRadius: 8,
      boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
      zIndex: 9998,
      pointerEvents: 'none' as const,
      transition: 'all 0.3s ease',
    };
  };

  const highlightStyle = getHighlightStyle();

  return (
    <>
      <style>{`@keyframes tourFadeIn { from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }`}</style>

      {/* Backdrop */}
      {!highlightStyle && <div style={styles.backdrop} onClick={finish} />}

      {/* Highlight ring */}
      {highlightStyle && <div style={highlightStyle} />}

      {/* Tooltip */}
      <div style={getTooltipStyle()}>
        <div style={styles.header}>
          <h3 style={styles.title}>{step.title}</h3>
          <button style={styles.closeBtn} onClick={finish} title="Exit tour">&times;</button>
        </div>
        <div style={styles.body}>{step.content}</div>

        {/* Progress dots */}
        <div style={styles.dots}>
          {steps.map((_, i) => (
            <div key={i} style={i === currentStep ? styles.dotActive : styles.dot} />
          ))}
        </div>

        <div style={styles.footer}>
          <span style={styles.progress}>{currentStep + 1} of {steps.length}</span>
          <div style={styles.buttons}>
            {currentStep > 0 && (
              <button style={styles.btnSecondary} onClick={() => setCurrentStep(c => c - 1)}>
                Back
              </button>
            )}
            {currentStep < steps.length - 1 ? (
              <button style={styles.btnPrimary} onClick={() => setCurrentStep(c => c + 1)}>
                Next
              </button>
            ) : (
              <button style={styles.btnPrimary} onClick={finish}>
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/** Returns true if the user has already completed the tour */
export function hasTourBeenCompleted(): boolean {
  return localStorage.getItem('cgs-expense-tour-completed') === 'true';
}
